import { useSeoMeta, useHead } from '@unhead/react';
import { SiteShell } from '@/components/SiteShell';
import { PageHero } from '@/components/PageHero';
import { normalizeCanonicalUrl } from '@/lib/seo';
import faqBg from '../../images/VIP House Movers Removal Team working.jpg';
import faqImg1 from '../../images/VIP House Movers Removal Team working.jpg';
import faqImg2 from '../../images/VIP House Movers Removal Van.jpg';
import faqImg3 from '../../images/VIP House Movers Team Packing.jpg';

export default function FAQ() {
  const faqs = [
    { q: "Are you fully insured?", a: "Yes, we hold comprehensive goods in transit and public liability insurance. Your belongings are fully protected from the moment they are in our care until they are safely placed in your new home." },
    { q: "Do you supply packing materials or packing services?", a: "We offer both! We can provide high-quality boxes, bubble wrap, and tape if you wish to pack yourself, or our expert team can provide a full or partial packing service to save you time and stress." },
    { q: "Can you dismantle and reassemble my furniture?", a: "Absolutely. Our professional moving team comes fully equipped with the right tools to safely dismantle large items like beds and wardrobes, and carefully reassemble them in your new property." },
    { q: "How far in advance should I book my move?", a: "We recommend booking 2-4 weeks in advance to secure your preferred moving date, especially for Fridays and end-of-month moves. However, we always aim to accommodate last-minute or short-notice moves when we have availability." },
    { q: "Are your moving services eco-friendly?", a: "Yes! As an eco-friendly company, we are committed to reducing our environmental impact. We use sustainable, recyclable packing materials, optimize our transport routes for fuel efficiency, and ensure responsible disposal and recycling of unwanted items during whole-house or office clearances." },
    { q: "Do you operate on weekends or Bank Holidays?", a: "Yes, we understand that moving during the week isn't always possible. We offer weekend and Bank Holiday moves subject to availability." },
    { q: "How long does a typical house move take?", a: "It depends on the volume of belongings and distance. A 2-bedroom local move often takes around half a day, while larger 4+ bedroom homes take a full day. We assess this during your quote process." }
  ];

  useSeoMeta({
    title: 'Removals FAQs | House Removals Questions Wolverhampton | VIP House Movers',
    description: 'Frequently asked questions about house removals, packing, insurance, moving services and bookings with VIP House Movers. Serving Wolverhampton & West Midlands.',
    ogTitle: 'Removals FAQs | VIP House Movers Wolverhampton',
    ogDescription: 'Answers to your house removal questions. Insurance, packing, eco-friendly practices, booking & more from VIP House Movers Wolverhampton.',
    ogImage: 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: 'https://www.viphousemovers.com/faq',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Removals FAQs | VIP House Movers Wolverhampton',
    twitterDescription: 'Removal FAQs answered by VIP House Movers Wolverhampton.',
    twitterImage: 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl('https://www.viphousemovers.com/faq');

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: 'removals FAQ, moving questions, packing questions, removal service FAQ, house removal advice, Wolverhampton moving questions, insured removals FAQ, moving company FAQs',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map(f => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a }
          }))
        })
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.viphousemovers.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'FAQ',
              item: 'https://www.viphousemovers.com/faq',
            },
          ],
        }),
      },
    ]
  });

  return (
    <SiteShell activePath="/faq" fixedNav>
      <PageHero
        eyebrow="Help & FAQs"
        title="Everything you need to know about your move"
        description="Quick answers about insurance, packing, booking, eco-friendly practices, and how VIP House Movers delivers a premium experience on every move."
        align="center"
        backgroundImage={faqBg}
      />

      <div className="max-w-6xl mx-auto px-4 py-20">
        {/* Illustrative image strip for richer content + internal trust */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {[
            {
              src: faqImg1,
              alt: 'Professional VIP House Movers crew answering questions about insurance, packing and UK removal services',
              caption: 'Expert advice',
            },
            {
              src: faqImg2,
              alt: 'VIP House Movers removal van on a UK street during a scheduled house move',
              caption: 'Reliable, insured moves',
            },
            {
              src: faqImg3,
              alt: 'Eco-friendly packing materials being prepared by VIP House Movers for a Wolverhampton relocation',
              caption: 'Eco-friendly packing',
            },
          ].map((img) => (
            <figure key={img.src} className="relative overflow-hidden rounded-2xl shadow-md border border-gray-100">
              <img
                src={img.src}
                alt={img.alt}
                width={1200}
                height={800}
                loading="lazy"
                decoding="async"
                className="w-full h-56 object-cover"
              />
              <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-sm font-semibold p-4">
                {img.caption}
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <details key={i} className="group bg-white rounded-xl shadow-sm border border-gray-100 p-6 cursor-pointer">
              <summary className="font-bold text-lg text-gray-900 list-none flex justify-between items-center">
                {faq.q}
                <span className="text-[#D4AF37] group-open:-rotate-180 transition-transform duration-300">▼</span>
              </summary>
              <p className="text-gray-600 mt-4 leading-relaxed font-light">{faq.a}</p>
            </details>
          ))}
        </div>
      </div>
    </SiteShell>
  );
}

